import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Upload, Search, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusPill } from "@/components/common/StatusPill";
import { EmptyState } from "@/components/common/EmptyState";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { teamColour } from "@/lib/constants";
import { TaskGroupsEditor, NextStepsEditor } from "@/pages/admin/FlowEditors";
import { EditDialog, KEY } from "@/pages/admin/CatalogueEditDialog";
import { cn } from "@/lib/utils";

const FILTERS = [["live", "Live"], ["soon", "Coming soon"], ["published", "Published"], ["visible", "Visible"], ["sub", "Sub-agents"]];
const PASS = {
  live: (d) => d.live, soon: (d) => !d.live, published: (d) => d.published, visible: (d) => d.visible !== false, sub: (d) => !!d.parent,
};

const Edited = ({ doc }) => (
  <>
    {doc.visible === false && <span className="text-xs font-medium text-warning" data-testid="hidden-badge">Hidden from users</span>}
    {doc.edited_fields?.length ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={doc.edited_fields.join(", ")}><span className="h-1.5 w-1.5 rounded-full bg-action" aria-hidden="true" />Edited in hub</span> : null}
  </>
);

const EntryTable = ({ coll, teams, rows, teamsById, selected, setSelected, onEdit }) => {
  const nameOf = (d) => (coll === "agents" ? d.chosen_name || d.working_name : d.name);
  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const groups = [...teams.map((t) => [t.id, t.section || t.name]), [null, "No team"]].map(([id, label]) => [id, label, rows.filter((d) => (d.team || null) === id || (id === null && !teamsById[d.team]))]).filter(([, , list]) => list.length);
  if (!groups.length) return <p className="p-6 text-sm text-muted-foreground">Nothing matches. Clear a filter or the search.</p>;
  return (
    <Table>
      <TableHeader><TableRow><TableHead className="w-10"><span className="sr-only">Select</span></TableHead><TableHead>{coll === "agents" ? "Agent" : "Workflow"}</TableHead><TableHead>Status</TableHead><TableHead>Order</TableHead><TableHead /></TableRow></TableHeader>
      <TableBody>
        {groups.map(([tid, label, list]) => [
          <TableRow key={`h-${tid}`} className="bg-muted/60 hover:bg-muted/60" data-testid={`catalogue-group-${coll}-${tid}`}>
            <TableCell colSpan={4} className="py-2 text-sm font-semibold"><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: teamColour(teamsById[tid], teamsById[tid]?._index) }} aria-hidden="true" />{label} <span className="font-normal text-muted-foreground">{list.length}</span></TableCell>
            <TableCell className="py-2 text-right">{tid && <Button variant="ghost" size="sm" onClick={() => downloadFile(`/admin/catalogue/export/teams/${tid}`, `${tid}.json`)} data-testid={`catalogue-export-team-${tid}`}><Download className="!h-4 !w-4" /> Export team</Button>}</TableCell>
          </TableRow>,
          ...[...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)).map((d) => {
            const k = d[KEY[coll]];
            return (
              <TableRow key={k} data-testid={`catalogue-${coll === "agents" ? "agent" : "workflow"}-row-${k}`}>
                <TableCell><input type="checkbox" checked={selected.includes(k)} onChange={() => toggle(k)} aria-label={`Select ${nameOf(d)}`} data-testid={`catalogue-select-${k}`} /></TableCell>
                <TableCell><div className="flex items-center gap-3"><EntityAvatar entity={d} name={nameOf(d)} size="sm" /><div><p className="font-medium">{nameOf(d)}</p><p className="text-xs text-muted-foreground">{k}{d.kind ? ` · ${d.kind}` : ""}{d.parent ? ` · sub-agent of ${d.parent}` : ""}</p></div></div></TableCell>
                <TableCell><div className="flex flex-col items-start gap-1"><StatusPill status={d.live ? "live" : "soon"} testid={`catalogue-status-${k}`} />{!d.published && <span className="text-xs text-muted-foreground">Unpublished</span>}<Edited doc={d} /></div></TableCell>
                <TableCell className="text-sm tabular-nums">{d.display_order}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => onEdit({ coll, doc: d, name: nameOf(d) })} data-testid={`catalogue-${coll === "agents" ? "agent" : "workflow"}-edit-${k}`}><Pencil className="!h-4 !w-4" /> Edit</Button></TableCell>
              </TableRow>
            );
          }),
        ])}
      </TableBody>
    </Table>
  );
};

const EntryList = ({ coll, onEdit }) => {
  const { teams, teamsById, reload, ...cat } = useCatalogue();
  const [q, setQ] = useState("");
  const [on, setOn] = useState([]);
  const [selected, setSelected] = useState([]);
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return cat[coll].filter((d) => !n || `${d.chosen_name || ""} ${d.working_name || ""} ${d.name || ""} ${d[KEY[coll]]}`.toLowerCase().includes(n)).filter((d) => on.every((f) => PASS[f](d)));
  }, [cat, coll, q, on]);
  const bulk = async (action) => {
    try { const { data } = await api.post("/admin/catalogue/bulk", { collection: coll, keys: selected, action }); toast.success(`${data.changed} updated`); setSelected([]); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 !h-4 !w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search by name or id" aria-label={`Search ${coll}`} data-testid={`catalogue-search-${coll}`} />
        </div>
        {FILTERS.filter(([f]) => coll === "agents" || f !== "sub").map(([f, l]) => (
          <button key={f} type="button" aria-pressed={on.includes(f)} onClick={() => setOn((s) => (s.includes(f) ? s.filter((x) => x !== f) : [...s, f]))}
            className={cn("rounded-full border px-3 py-1.5 text-sm transition-quick hover:bg-muted", on.includes(f) && "border-foreground bg-muted font-medium")} data-testid={`catalogue-filter-${coll}-${f}`}>{l}</button>
        ))}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card px-3 py-2 text-sm" data-testid={`catalogue-bulk-${coll}`}>
          <span className="font-medium">{selected.length} selected</span>
          {[["publish", "Publish"], ["unpublish", "Unpublish"], ["show", "Show"], ["hide", "Hide"]].map(([a, l]) => <Button key={a} variant="secondary" size="sm" onClick={() => bulk(a)} data-testid={`catalogue-bulk-${a}`}>{l}</Button>)}
          <Button variant="ghost" size="sm" onClick={() => setSelected([])}>Clear</Button>
        </div>
      )}
      <div className="card-elevated overflow-x-auto"><EntryTable coll={coll} teams={teams} rows={rows} teamsById={teamsById} selected={selected} setSelected={setSelected} onEdit={onEdit} /></div>
    </div>
  );
};

export default function CatalogueAdmin() {
  const { teams, agents, workflows, loading, isEmpty } = useCatalogue();
  const [edit, setEdit] = useState(null);
  if (loading) return <Skeleton className="h-64 rounded-xl" />;
  if (isEmpty) {
    return <EmptyState icon={Upload} title="Nothing to edit yet" body="Import the catalogue file first. Teams, agents and workflows will then be editable here." action={<Button asChild><Link to="/admin/import" data-testid="catalogue-go-import">Import catalogue</Link></Button>} testid="catalogue-admin-empty" />;
  }
  return (
    <div data-testid="admin-catalogue">
      <Tabs defaultValue="agents">
        <TabsList className="h-auto flex-wrap rounded-xl">
          <TabsTrigger value="agents" data-testid="catalogue-tab-agents">Agents ({agents.length})</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="catalogue-tab-workflows">Workflows ({workflows.length})</TabsTrigger>
          <TabsTrigger value="teams" data-testid="catalogue-tab-teams">Teams ({teams.length})</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="catalogue-tab-tasks">Task groups</TabsTrigger>
          <TabsTrigger value="next" data-testid="catalogue-tab-next">Next steps</TabsTrigger>
        </TabsList>
        <TabsContent value="agents" className="mt-4"><EntryList coll="agents" onEdit={setEdit} /></TabsContent>
        <TabsContent value="workflows" className="mt-4"><EntryList coll="workflows" onEdit={setEdit} /></TabsContent>
        <TabsContent value="teams" className="card-elevated mt-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Team</TableHead><TableHead>Lead</TableHead><TableHead>Published</TableHead><TableHead>Order</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {teams.map((t, i) => (
                <TableRow key={t.id} data-testid={`catalogue-team-row-${t.id}`}>
                  <TableCell><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: teamColour(t, i) }} aria-hidden="true" /><span className="font-medium">{t.section || t.name}</span> <span className="text-xs text-muted-foreground">{t.id}</span></TableCell>
                  <TableCell className="text-sm">{t.lead || "Not set"}</TableCell>
                  <TableCell className="text-sm">{t.published ? "Yes" : "No"} <Edited doc={t} /></TableCell>
                  <TableCell className="text-sm tabular-nums">{t.order}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="sm" onClick={() => downloadFile(`/admin/catalogue/export/teams/${t.id}`, `${t.id}.json`)} data-testid={`catalogue-team-export-${t.id}`}><Download className="!h-4 !w-4" /> Export team</Button>
                    <Button variant="ghost" size="sm" onClick={() => setEdit({ coll: "teams", doc: t, name: t.section || t.name })} data-testid={`catalogue-team-edit-${t.id}`}><Pencil className="!h-4 !w-4" /> Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>
        <TabsContent value="tasks" className="mt-4"><TaskGroupsEditor /></TabsContent>
        <TabsContent value="next" className="mt-4"><NextStepsEditor /></TabsContent>
      </Tabs>
      <EditDialog edit={edit} onClose={() => setEdit(null)} />
    </div>
  );
}
